import React, { useState, useContext, useEffect, useRef, useMemo } from 'react'
import {
  Modal,
  Box,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { createMessage } from './TechMessageService'
import PreviousTechnicianMessagesList from './PreviousTechnicianMessagesList'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { fetchData } from '../../../global/utils/fetch'
import {
  TechnicianResponse,
  getAllLocations,
  LocationResponse,
  getTechniciansByLocation,
} from './api'
import styles from './styles.module.css'
import { ITechnicianPartial } from '../../../../../app/entities/Technician'
import { ILocationPartial } from '../../../../../app/entities/Location'
import { TechLink } from '../../../../../app/entities/TechnicianMessage'

interface MessageModalProps {
  open: boolean
  onClose: () => void
}

const MessageModal: React.FC<MessageModalProps> = ({ open, onClose }) => {
  const { user } = useContext(UserContext)
  const [activeTab, setActiveTab] = useState<number>(0)
  const [title, setTitle] = useState<string>('')
  const [body, setBody] = useState<string>('')
  const [links, setLinks] = useState<TechLink[]>([])
  const [sending, setSending] = useState<boolean>(false)
  const [refreshListTrigger, setRefreshListTrigger] = useState<number>(0)
  const [locations, setLocations] = useState<ILocationPartial[]>([])
  const [allTechnicians, setAllTechnicians] = useState<ITechnicianPartial[]>([])
  const [recipientType, setRecipientType] = useState<
    'all' | 'location' | 'individual'
  >('all')
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [selectedTechnicians, setSelectedTechnicians] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const technicianListRef = useRef<HTMLDivElement>(null)

  const technicianMap = useMemo(
    () => new Map(allTechnicians.map((tech) => [tech.objectId, tech])),
    [allTechnicians],
  )

  // For link input
  const [linkText, setLinkText] = useState<string>('')
  const [linkUrl, setLinkUrl] = useState<string>('')

  // Scheduling specific time to send Technician message

  const [scheduleType, setScheduleType] = useState<'immediate' | 'scheduled'>(
    'immediate',
  )
  const [scheduledDate, setScheduledDate] = useState<string>('')

  useEffect(() => {
    if (open) {
      const fetchRecipientData = async () => {
        setIsLoading(true)

        try {
          // Fetch locations first
          const locationData = await fetchData<LocationResponse>(
            getAllLocations(),
          )

          // Process locations
          let allLocations: ILocationPartial[] = []
          if (locationData && Array.isArray(locationData)) {
            allLocations = locationData as ILocationPartial[]
          } else if (
            locationData &&
            locationData.items &&
            Array.isArray(locationData.items)
          ) {
            allLocations = locationData.items
          } else if (
            locationData &&
            locationData.locations &&
            Array.isArray(locationData.locations)
          ) {
            allLocations = locationData.locations
          }

          // Filter for active locations only and sort alphabetically
          const activeLocations = allLocations
            .filter((location) => location.active !== false)
            .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)

          setLocations(activeLocations)

          // Fetch technicians from all locations for "By Technicians" option
          const allTechnicianPromises = activeLocations.map((location) =>
            fetchData<TechnicianResponse>(
              getTechniciansByLocation(location.objectId!),
            ),
          )

          const allTechnicianResponses = await Promise.all(
            allTechnicianPromises,
          )

          // Combine all technicians from all locations
          let allTechniciansFromAllLocations: ITechnicianPartial[] = []
          allTechnicianResponses.forEach((response) => {
            if (response && response.items && Array.isArray(response.items)) {
              allTechniciansFromAllLocations.push(...response.items)
            } else if (Array.isArray(response)) {
              allTechniciansFromAllLocations.push(
                ...(response as ITechnicianPartial[]),
              )
            }
          })

          // Sort technicians alphabetically by name
          const sortedTechnicians = allTechniciansFromAllLocations.sort(
            (a, b) => a.name?.localeCompare(b.name || '') || 0,
          )

          setAllTechnicians(sortedTechnicians)
          setIsLoading(false)
        } catch (error) {
          console.error('Error fetching data:', error)
          setLocations([])
          setAllTechnicians([])
          setIsLoading(false)
        }
      }

      fetchRecipientData()
    }
  }, [open])

  const handleAddLink = () => {
    if (!linkText.trim() || !linkUrl.trim()) {
      return
    }

    setLinks([
      ...links,
      {
        id: `link-${Date.now()}`,
        text: linkText,
        url: linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`,
      },
    ])

    // Clear inputs
    setLinkText('')
    setLinkUrl('')
  }

  // Recipient selector components
  const RecipientSelector = () => {
    if (isLoading) {
      return <div className={styles.loading}>Loading recipients...</div>
    }

    return (
      <>
        {/* Step 1: Who are you messaging? */}
        <div className={styles.formField}>
          <h4 className={styles.sectionTitle}>Message Recipient</h4>
          <div className={styles.scheduleOptions}>
            <div>
              <label className={styles.radioLabel}>
                <input
                  type='radio'
                  name='recipientType'
                  value='all'
                  checked={recipientType === 'all'}
                  onChange={() => {
                    setRecipientType('all')
                    setSelectedLocation('')
                    setSelectedTechnicians([])
                  }}
                />
                All Technicians
              </label>

              <label className={styles.radioLabel}>
                <input
                  type='radio'
                  name='recipientType'
                  value='location'
                  checked={recipientType === 'location'}
                  onChange={() => {
                    setRecipientType('location')
                    setSelectedLocation('')
                    setSelectedTechnicians([])
                  }}
                />
                By Location
              </label>

              <label className={styles.radioLabel}>
                <input
                  type='radio'
                  name='recipientType'
                  value='individual'
                  checked={recipientType === 'individual'}
                  onChange={() => {
                    setRecipientType('individual')
                    setSelectedLocation('')
                    setSelectedTechnicians([])
                  }}
                />
                By Technicians
              </label>
            </div>
          </div>
        </div>

        {/* Step 2: Location Selection (when "By Location" is selected) */}
        {recipientType === 'location' && (
          <div className={styles.formField}>
            <h4 className={styles.sectionTitle}>Select Location</h4>
            <TextField
              fullWidth
              select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className={styles.input}
            >
              <MenuItem value=''>
                <em>Choose a location</em>
              </MenuItem>
              {locations.map((location) => (
                <MenuItem
                  key={location.objectId}
                  value={location.objectId}
                >
                  {location.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
        )}

        {/* Step 2: Individual Technician Selection */}
        {recipientType === 'individual' && (
          <div className={styles.formField}>
            <h4 className={styles.sectionTitle}>Select Technicians</h4>
            <div className={styles.technicianSelectionContainer}>
              {/* Left Box - All Technicians */}
              <div className={styles.technicianBox}>
                <h5 className={styles.technicianBoxHeader}>
                  Available Technicians
                </h5>
                <div
                  ref={technicianListRef}
                  className={styles.technicianBoxContent}
                >
                  {allTechnicians.map(
                    (technician) =>
                      technician.objectId && (
                        <div key={technician.objectId}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedTechnicians.includes(
                                  technician.objectId,
                                )}
                                onChange={(e) => {
                                  // Preserve scroll position
                                  const scrollTop =
                                    technicianListRef.current?.scrollTop || 0

                                  if (e.target.checked) {
                                    setSelectedTechnicians((prev) => {
                                      const newSelection = [...prev]
                                      newSelection.push(technician.objectId!)
                                      return newSelection
                                    })
                                  } else {
                                    setSelectedTechnicians((prev) =>
                                      prev.filter(
                                        (id) => id !== technician.objectId,
                                      ),
                                    )
                                  }

                                  // Restore scroll position after state update
                                  setTimeout(() => {
                                    if (technicianListRef.current) {
                                      technicianListRef.current.scrollTop =
                                        scrollTop
                                    }
                                  }, 0)
                                }}
                                sx={{
                                  color: '#D70000',
                                  '&.Mui-checked': { color: '#D70000' },
                                  '& .MuiSvgIcon-root': { fontSize: 18 },
                                }}
                              />
                            }
                            label={`${technician.name || 'Unknown'}${
                              technician.location?.name
                                ? ` (${technician.location.name})`
                                : ''
                            }`}
                          />
                        </div>
                      ),
                  )}
                </div>
              </div>

              {/* Right Box - Selected Technicians */}
              <div className={styles.technicianBox}>
                <h5 className={styles.technicianBoxHeader}>
                  Selected Technicians
                </h5>
                <div className={styles.technicianBoxContent}>
                  {selectedTechnicians.length === 0 ? (
                    <div className={styles.emptySelectionText}>
                      No technicians selected
                    </div>
                  ) : (
                    selectedTechnicians.map((technicianId) => {
                      const technician = technicianMap.get(technicianId)
                      return technician ? (
                        <div
                          key={technicianId}
                          className={styles.selectedTechnicianItem}
                        >
                          {`${technician.name || 'Unknown'}${
                            technician.location?.name
                              ? ` (${technician.location.name})`
                              : ''
                          }`}
                        </div>
                      ) : null
                    })
                  )}
                  <div className={styles.technicianCount}>
                    {selectedTechnicians.length} of {allTechnicians.length}{' '}
                    technicians selected
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  const ScheduleSelector = () => (
    <div className={styles.formField}>
      <h4 className={styles.sectionTitle}>Delivery Schedule</h4>
      <div className={styles.scheduleOptions}>
        <div>
          <label className={styles.radioLabel}>
            <input
              type='radio'
              name='scheduleType'
              value='immediate'
              checked={scheduleType === 'immediate'}
              onChange={() => setScheduleType('immediate')}
            />
            Send immediately
          </label>

          <label className={styles.radioLabel}>
            <input
              type='radio'
              name='scheduleType'
              value='scheduled'
              checked={scheduleType === 'scheduled'}
              onChange={() => setScheduleType('scheduled')}
            />
            Schedule for later
          </label>
        </div>

        {scheduleType === 'scheduled' && (
          <TextField
            InputLabelProps={{ shrink: true }}
            type='date'
            label='Scheduled Date'
            name='scheduledDate'
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            helperText='Message will be sent on this date'
            size='small'
            style={{ marginTop: '8px' }}
          />
        )}
      </div>
    </div>
  )

  const handleRemoveLink = (id: string) => {
    setLinks(links.filter((link) => link.id !== id))
  }

  const isFormValid = () => {
    // Check required fields
    if (!title.trim() || !body.trim()) {
      return false
    }

    // Check schedule validation
    if (scheduleType === 'scheduled' && !scheduledDate) {
      return false
    }

    // Check recipient validation
    if (recipientType === 'location' && !selectedLocation) {
      return false
    }

    if (recipientType === 'individual' && selectedTechnicians.length === 0) {
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      alert('Please enter a title and message body')
      return
    }

    // Validate recipient selection
    if (recipientType === 'location' && !selectedLocation) {
      alert('Please select a location')
      return
    }

    if (recipientType === 'individual' && selectedTechnicians.length === 0) {
      alert('Please select at least one technician')
      return
    }

    // Add schedule date validation
    let scheduleDate: Date | undefined
    if (scheduleType === 'scheduled') {
      if (!scheduledDate) {
        alert('Please select a valid date for scheduling')
        return
      }

      const selected = new Date(scheduledDate)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (selected < today) {
        alert('Please choose a future date')
        return
      }

      scheduleDate = selected
    }

    setSending(true)

    // Build targetAudience based on selection type
    let targetAudience = 'all'
    if (recipientType === 'all') {
      targetAudience = 'all'
    } else if (recipientType === 'location') {
      // All technicians at location
      targetAudience = `location:${selectedLocation}`
    } else if (recipientType === 'individual') {
      // Specific technicians selected
      targetAudience = `selected:${selectedTechnicians.join(',')}`
    }

    await createMessage({
      title,
      body,
      links,
      createdBy: user?.name || 'Admin User',
      targetAudience,
      scheduleType,
      scheduleDate,
      requiresAcknowledgment: true,
    })

    // Reset form
    setTitle('')
    setBody('')
    setLinks([])
    setRecipientType('all')
    setSelectedLocation('')
    setSelectedTechnicians([])
    setScheduleType('immediate')
    setScheduledDate('')

    setRefreshListTrigger((prev) => prev + 1)
    setActiveTab(1)

    alert(
      scheduleType === 'immediate'
        ? 'Message has been sent successfully'
        : `Message has been scheduled for ${scheduledDate}`,
    )

    setSending(false)
  }

  const handleClose = () => {
    // Reset form when closing
    setTitle('')
    setBody('')
    setLinks([])
    setRecipientType('all')
    setSelectedLocation('')
    setSelectedTechnicians([])
    setActiveTab(0)
    setScheduleType('immediate')
    setScheduledDate('')
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='technician-message-modal'
    >
      <Box className={styles.messageModal}>
        <div className={styles.modalTitle}>Technician Messaging</div>

        <div className={styles.tabContainer}>
          <button
            className={`${styles.tabButton} ${
              activeTab === 0 ? styles.active : ''
            }`}
            onClick={() => setActiveTab(0)}
          >
            Create Message
          </button>
          <button
            className={`${styles.tabButton} ${
              activeTab === 1 ? styles.active : ''
            }`}
            onClick={() => setActiveTab(1)}
          >
            View Messages
          </button>
        </div>

        <div className={styles.contentArea}>
          {activeTab === 0 ? (
            <div>
              <h3 className={styles.sectionTitle}>Create New Message</h3>
              <p className={styles.sectionDescription}>
                This message will be shown to technicians after they complete
                their DVIR.
              </p>

              <div className={styles.formField}>
                <TextField
                  autoFocus
                  label='Title'
                  type='text'
                  fullWidth
                  variant='outlined'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>

              <div className={styles.formField}>
                <TextField
                  label='Message Content'
                  multiline
                  rows={4}
                  fullWidth
                  variant='outlined'
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                />
              </div>

              <ScheduleSelector />

              <RecipientSelector />

              <div className={styles.formField}>
                <h4 className={styles.sectionTitle}>Add Links (Optional)</h4>
                <p className={styles.sectionDescription}>
                  Add links that technicians must click before acknowledging the
                  message.
                </p>

                <div className={styles.linkContainer}>
                  <TextField
                    size='small'
                    label='Link Text'
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    style={{ marginRight: '8px', flex: 1 }}
                  />
                  <TextField
                    size='small'
                    label='URL'
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    style={{ marginRight: '8px', flex: 1 }}
                  />
                  <button
                    // type='button'
                    className={styles.addButton}
                    onClick={handleAddLink}
                  >
                    <AddIcon fontSize='small' /> Add
                  </button>
                </div>

                {links.length > 0 && (
                  <div>
                    <h5>Added Links:</h5>
                    <ul className={styles.linksList}>
                      {links.map((link) => (
                        <li
                          key={link.id}
                          className={styles.linkItem}
                        >
                          <span>
                            {link.text} -{' '}
                            <span className={styles.linkUrl}>{link.url}</span>
                          </span>
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleRemoveLink(link.id)}
                            aria-label='delete link'
                          >
                            <DeleteIcon fontSize='small' />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <PreviousTechnicianMessagesList
              refreshTrigger={refreshListTrigger}
              onRefresh={() => setRefreshListTrigger((prev) => prev + 1)}
              locations={locations}
              allTechnicians={allTechnicians}
            />
          )}
        </div>

        <div className={styles.footerContainer}>
          <button
            className={styles.buttonCancel}
            onClick={handleClose}
          >
            Cancel
          </button>
          {activeTab === 0 && (
            <button
              className={styles.buttonPrimary}
              onClick={handleSubmit}
              disabled={sending || !isFormValid()}
            >
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          )}
        </div>
      </Box>
    </Modal>
  )
}

export default MessageModal
